import { Box } from '@chakra-ui/react';

import { formatCurrency } from '@/utils/number-formatter';

import { LifecycleRow } from './buildPositionLifeCycleColumns';

// Validated dark-mode palette (CVD-safe adjacent pairs on the Chakra dark surface)
const KEPT_COLOR = '#3987E5';
const SOLD_COLOR = '#D95926';
const TLH_SELL_COLOR = '#E8C547';
const TARGET_MARKER_COLOR = 'whiteAlpha.800';

// Symlog compression so tiny positions stay visible next to large ones.
const symlogFraction = (value: number, max: number) =>
    max <= 0 ? 0 : Math.log10(1 + Math.max(0, value)) / Math.log10(1 + max);

/**
 * Compact bar of the current exposure decomposed into kept (blue) and sold, with the TLH-driven
 * part of the sell highlighted in gold (the rest of the sell is orange). The white tick marks the
 * final target: the gold segment ends at it, and an underweight position leaves empty track up
 * to it.
 *
 * The bar's total length is symlog-compressed for cross-row comparability, but the split inside
 * a row is linear in dollars so the composition reads true.
 */
export const ExposureBar = ({ row, max }: { row: LifecycleRow; max: number }) => {
    const current = row.currentExposure ?? 0;
    const target = row.finalTargetExposure ?? 0;

    // Executed sell (decision-time, after sell constraints); buys/holds have finalActive <= 0
    const sold = Math.min(Math.max(0, row.finalActiveExposure ?? 0), current);
    // The part of the sell below the final target is TLH by construction
    const tlhSell = Math.max(0, sold - Math.max(0, current - target));
    const kept = current - sold;

    // Total length is symlog vs the grid max; the inside splits linearly by dollars
    const span = Math.max(current, target);
    const dollarsToWidth = span > 0 ? symlogFraction(span, max) / span : 0;
    const widthPct = (dollars: number) => `${dollars * dollarsToWidth * 100}%`;

    const soldText =
        sold > 0 ? ` / sold ${formatCurrency(sold)}${tlhSell > 0 ? ` (tlh ${formatCurrency(tlhSell)})` : ''}` : '';
    const title = `current ${formatCurrency(current)} / final target ${formatCurrency(target)}${soldText}`;

    return (
        <Box
            title={title}
            position="relative"
            display="flex"
            width="170px"
            height="12px"
            background="whiteAlpha.100"
            borderRadius="sm"
            overflow="hidden"
        >
            <Box width={widthPct(kept)} background={KEPT_COLOR} flexShrink={0} />
            <Box width={widthPct(tlhSell)} background={TLH_SELL_COLOR} flexShrink={0} />
            <Box width={widthPct(sold - tlhSell)} background={SOLD_COLOR} flexShrink={0} />
            {target > 0 && (
                <Box
                    position="absolute"
                    left={widthPct(target)}
                    top={0}
                    bottom={0}
                    width="2px"
                    marginLeft="-1px"
                    background={TARGET_MARKER_COLOR}
                />
            )}
        </Box>
    );
};
